import React, { useState } from "react";
import { Loader2, Mail, ArrowRight, Check, Gift } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, formatApiError } from "../../lib/api";

const GOLD = "#D49B3C";
const NAVY = "#102945";

const UNLOCKS = [
  "Your MoneyMind Persona Chart — planning, follow-through, savings consistency, risk response, decision confidence",
  "How your persona can advance — credit health, savings growth, financial security",
  "Three personalized habits, one for each",
  "Your 4-week persona plan — week 1 free",
  "Your 60-day free pass to the full app — no card, no bank login",
];

/**
 * Inline soft-gate unlock form — rendered on the QuizSoftReveal page,
 * below the instantly-revealed persona card. Email unlocks the full
 * breakdown, the 4-week plan, and (if the guess was right) locks in
 * the monthly contest entry.
 *
 * Props
 *   personaPreview   { name, icon, tagline, color, contest_guess_correct? }
 *   leadToken        opaque token issued by /quiz/submit
 *   onResult(result) called with the full persona payload after success
 */
export default function QuizEmailGate({ personaPreview, leadToken, onResult }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscribeBlog, setSubscribeBlog] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const shortName = (personaPreview?.name || "your persona").replace(/^The /, "");
  const guessCorrect = personaPreview?.contest_guess_correct === true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post("/quiz/claim-result", {
        lead_token: leadToken,
        email: email.trim(),
        name: name.trim() || null,
        subscribe_blog: subscribeBlog,
      });
      onResult?.({ ...r.data, lead_email: email.trim() });
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <section
      className="mt-4 bg-white rounded-xl shadow-md overflow-hidden"
      data-testid="quiz-email-gate"
    >
      {/* Brand bar */}
      <div className="px-6 py-3 flex items-center gap-2" style={{ background: NAVY }}>
        <Mail className="h-4 w-4" style={{ color: GOLD }} />
        <span className="text-white text-sm font-medium">Unlock your full breakdown + 60-day free pass</span>
      </div>

      <form onSubmit={handleSubmit} className="p-6 sm:p-8">
        <h2 className="font-serif text-2xl text-[#1B2A3A] leading-snug">
          Your {shortName} result goes much deeper.
        </h2>
        <p className="mt-2 text-sm text-[#4d4f4c] leading-relaxed" data-testid="quiz-email-gate-lede">
          Unlock your MoneyMind Persona Chart and see how your pattern may influence
          credit health, savings growth, and financial security — plus
          <strong> 60 days of the full app free, no card</strong>. We&apos;ll send
          a copy to your inbox too.
        </p>

        <ul className="mt-4 space-y-2" data-testid="quiz-email-gate-unlock-list">
          {UNLOCKS.map((u) => (
            <li key={u} className="flex items-start gap-2 text-sm text-[#2C302E]">
              <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#3E6B37" }} />
              <span>{u}</span>
            </li>
          ))}
        </ul>

        {guessCorrect && (
          <div
            className="mt-4 flex items-start gap-2.5 p-3 bg-[#F0F5EE] border border-[#607762]/40 rounded-md"
            data-testid="quiz-email-gate-contest-lock"
          >
            <Gift className="h-4 w-4 mt-0.5 shrink-0 text-[#3E6B37]" />
            <p className="text-sm text-[#2C302E] leading-relaxed">
              <strong>Your guess was right</strong> — this also locks in your
              entry for this month&apos;s free-year drawing ($120 value).
            </p>
          </div>
        )}

        <label className="block text-sm font-medium text-[#2C302E] mt-5 mb-1">
          Email address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          className="w-full px-3 py-2.5 border border-[#E6E3DB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D49B3C] focus:border-transparent"
          data-testid="quiz-email-gate-email"
        />

        <label className="block text-sm font-medium text-[#2C302E] mt-4 mb-1">
          First name <span className="text-[#9b9892] font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="w-full px-3 py-2.5 border border-[#E6E3DB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D49B3C] focus:border-transparent"
          data-testid="quiz-email-gate-name"
        />

        <div className="mt-5 flex items-start gap-3 p-3 bg-[#FAEACB]/40 rounded-md border border-[#FAEACB]">
          <Switch
            checked={subscribeBlog}
            onCheckedChange={setSubscribeBlog}
            data-testid="quiz-email-gate-blog-toggle"
            aria-label="Subscribe to the weekly Finhabits blog post"
            className="mt-0.5"
          />
          <label className="text-sm text-[#2C302E] leading-relaxed cursor-pointer flex-1">
            <span className="font-medium">Send me the weekly Finhabits blog post.</span>
            <span className="block text-xs text-[#787671] mt-0.5">
              One short read per week on building money habits. Unsubscribe anytime.
            </span>
          </label>
        </div>

        {error && (
          <p
            className="mt-4 p-2.5 text-sm bg-[#FFF6F1] border border-[#C05C3D] text-[#C05C3D] rounded"
            data-testid="quiz-email-gate-error"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: GOLD, color: "#2A1A05" }}
          data-testid="quiz-email-gate-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Unlocking…
            </>
          ) : (
            <>
              Unlock My Full Breakdown
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>

        <p className="mt-4 text-[11px] text-[#9b9892] text-center leading-relaxed" data-testid="quiz-email-gate-privacy">
          By submitting you agree to receive your MoneyMind result by email.
          Your answers are private &mdash; never sold, never shared, and never
          used for ad targeting.
        </p>
      </form>
    </section>
  );
}
